import { useMemo, useState } from 'react';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  FolderInput,
  Eye,
  EyeOff,
  Globe,
  GlobeLock,
  Lock,
  MoreHorizontal,
  Pencil,
  Pin,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
  Unlock,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { pagesApi } from '../api';
import { pagesHooks, stickiesHooks, usePageAction } from '../queries';
import type { WikiPage } from '../types';

type MoveItem = { id: number; parent: number | null; order: number; visibility?: string };

// Pointer position at drag start — one drag at a time, so a module-level var
// suffices to measure travel and ignore accidental micro-drags.
let dragOrigin: { x: number; y: number } | null = null;

export interface WikiTreeHandlers {
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Re-parent within the tree; only available for collection page trees. */
  onShare?: (page: WikiPage) => void;
}

/**
 * Tree of wiki pages (parent/child). `flat` is used for archived/shared/recents
 * lists where the hierarchy isn't meaningful. Drag-and-drop re-parenting is on
 * for the non-flat tree, mirroring project Pages.
 */
export function WikiPageTree({
  pages,
  allPages,
  flat,
  canEdit,
  ...handlers
}: WikiTreeHandlers & {
  pages: WikiPage[];
  /** Full set (for descendant lookups + Move dialog). */
  allPages: WikiPage[];
  flat: boolean;
  canEdit: boolean;
}) {
  // The first top-level page is open by default (sub-pages show instantly);
  // `collapsed` records an explicit close of a default-open node so it sticks.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [movingPage, setMovingPage] = useState<WikiPage | null>(null);

  const firstRootId = useMemo(
    () => pages.filter((p) => p.parent == null).sort((a, b) => a.order - b.order)[0]?.id ?? null,
    [pages],
  );
  const expandedView = useMemo(() => {
    const next = new Set(expanded);
    if (firstRootId != null && !collapsed.has(firstRootId)) next.add(firstRootId);
    return next;
  }, [expanded, collapsed, firstRootId]);

  const toggleExpand = (id: number) => {
    const open = expandedView.has(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const childrenOf = (parentId: number | null) =>
    allPages
      .filter((p) => (p.parent ?? null) === parentId && !p.is_archived)
      .sort((a, b) => a.order - b.order);

  function descendantIds(rootId: number): Set<number> {
    const out = new Set<number>();
    const walk = (id: number) => {
      for (const c of allPages.filter((p) => p.parent === id)) {
        out.add(c.id);
        walk(c.id);
      }
    };
    walk(rootId);
    return out;
  }

  const reorder = usePageAction((items: MoveItem[]) => pagesApi.reorder(items));

  /** Expand the moved page's ancestors + select it, so a move never hides it. */
  function revealMovedPage(page: WikiPage, newParent: number | null) {
    const byId = new Map(allPages.map((p) => [p.id, p]));
    const chain: number[] = [];
    const seen = new Set<number>();
    let cur = newParent;
    while (cur != null && !seen.has(cur)) {
      seen.add(cur);
      chain.push(cur);
      cur = byId.get(cur)?.parent ?? null;
    }
    if (chain.length) {
      setExpanded((prev) => new Set([...prev, ...chain]));
      setCollapsed((prev) => new Set([...prev].filter((id) => !chain.includes(id))));
    }
    handlers.onSelect(page.id);
  }

  /** Apply a batch move atomically, reveal the page, and offer Undo. */
  function applyMove(page: WikiPage, items: MoveItem[], newParent: number | null) {
    const prev: MoveItem = {
      id: page.id,
      parent: page.parent ?? null,
      order: page.order,
      visibility: page.visibility,
    };
    reorder.mutate(items, {
      onSuccess: () => {
        revealMovedPage(page, newParent);
        toast.success(
          <span className="flex items-center gap-2">
            Moved “{page.title || 'Untitled'}”
            <button
              type="button"
              className="cursor-pointer font-medium underline"
              onClick={() => reorder.mutate([prev])}
            >
              Undo
            </button>
          </span>,
        );
      },
    });
  }

  /** Re-parent under `newParent` (null = top level); child inherits its visibility. */
  function moveToParent(page: WikiPage, newParent: number | null) {
    if (newParent != null && descendantIds(page.id).has(newParent)) return;
    const parent = newParent != null ? allPages.find((p) => p.id === newParent) : null;
    const order = childrenOf(newParent).filter((p) => p.id !== page.id).length;
    applyMove(
      page,
      [{ id: page.id, parent: newParent, order, ...(parent ? { visibility: parent.visibility } : {}) }],
      newParent,
    );
    setMovingPage(null);
  }

  function handleMove(dragId: number, targetId: number, position: 'before' | 'after' | 'inside') {
    if (dragId === targetId) return;
    const drag = allPages.find((p) => p.id === dragId);
    const target = allPages.find((p) => p.id === targetId);
    if (!drag || !target || descendantIds(dragId).has(targetId)) return;

    if (position === 'inside') {
      moveToParent(drag, targetId);
      return;
    }
    const newParent = target.parent ?? null;
    const parent = newParent != null ? allPages.find((p) => p.id === newParent) : null;
    const siblings = childrenOf(newParent).filter((p) => p.id !== dragId);
    const idx = siblings.findIndex((p) => p.id === targetId);
    const insertAt = position === 'before' ? idx : idx + 1;
    const ordered = [...siblings.slice(0, insertAt), drag, ...siblings.slice(insertAt)];
    const items: MoveItem[] = ordered.map((p, i) =>
      p.id === dragId
        ? { id: p.id, parent: newParent, order: i, ...(parent ? { visibility: parent.visibility } : {}) }
        : { id: p.id, parent: newParent, order: i },
    );
    applyMove(drag, items, newParent);
  }

  const create = pagesHooks.useCreate();
  function addChild(parentId: number) {
    const parent = allPages.find((p) => p.id === parentId);
    create.mutate(
      {
        collection: parent?.collection ?? null,
        parent: parentId,
        title: 'Untitled',
        visibility: parent?.visibility ?? 'public',
      },
      { onSuccess: (page) => handlers.onSelect(page.id) },
    );
    setExpanded((prev) => new Set(prev).add(parentId));
  }

  /** Menu "Move up/down": swap order with the adjacent sibling (same parent). */
  function moveStep(page: WikiPage, dir: 'up' | 'down') {
    const siblings = childrenOf(page.parent ?? null);
    const idx = siblings.findIndex((p) => p.id === page.id);
    const neighbor = dir === 'up' ? siblings[idx - 1] : siblings[idx + 1];
    if (neighbor) handleMove(page.id, neighbor.id, dir === 'up' ? 'before' : 'after');
  }

  const shared: SharedTreeProps = {
    ...handlers,
    flat,
    canEdit,
    expanded: expandedView,
    onToggleExpand: toggleExpand,
    onAddChild: addChild,
    onRequestMove: setMovingPage,
    draggingId,
    setDraggingId,
    onMove: handleMove,
    onMoveStep: moveStep,
  };

  if (flat) {
    return (
      <div className="space-y-0.5">
        {pages.map((p) => (
          <PageRow key={p.id} page={p} depth={0} hasChildren={false} {...shared} />
        ))}
      </div>
    );
  }

  const byParent = new Map<number | null, WikiPage[]>();
  for (const p of pages) {
    const key = p.parent ?? null;
    byParent.set(key, [...(byParent.get(key) ?? []), p]);
  }
  // Roots = true top-level pages PLUS orphans whose parent isn't in this list
  // (e.g. an archived parent), so archiving a parent lifts its children up
  // instead of hiding them. They re-nest on restore (the parent FK is untouched).
  const ids = new Set(pages.map((p) => p.id));
  const roots = pages.filter((p) => p.parent == null || !ids.has(p.parent));
  const render = (parent: number | null, depth: number): React.ReactNode => {
    const sibs = parent === null ? roots : (byParent.get(parent) ?? []);
    return sibs.map((p, i) => {
      const children = byParent.get(p.id) ?? [];
      const isCollapsed = !expandedView.has(p.id);
      return (
          <div key={p.id}>
            <PageRow
              page={p}
              depth={depth}
              hasChildren={children.length > 0}
              isCollapsed={isCollapsed}
              isFirst={i === 0}
              isLast={i === sibs.length - 1}
              {...shared}
            />
            {children.length > 0 && !isCollapsed ? render(p.id, depth + 1) : null}
          </div>
        );
    });
  };

  return (
    <>
      <div className="space-y-0.5">{render(null, 0)}</div>
      <MoveDialog
        page={movingPage}
        excludeIds={movingPage ? new Set([movingPage.id, ...descendantIds(movingPage.id)]) : new Set()}
        pages={allPages.filter((p) => !p.is_archived && p.collection === movingPage?.collection)}
        onMove={moveToParent}
        onClose={() => setMovingPage(null)}
      />
    </>
  );
}

interface SharedTreeProps extends WikiTreeHandlers {
  flat: boolean;
  canEdit: boolean;
  expanded: Set<number>;
  onToggleExpand: (id: number) => void;
  onAddChild: (parentId: number) => void;
  onRequestMove: (page: WikiPage) => void;
  draggingId: number | null;
  setDraggingId: (id: number | null) => void;
  onMove: (dragId: number, targetId: number, position: 'before' | 'after' | 'inside') => void;
  onMoveStep: (page: WikiPage, dir: 'up' | 'down') => void;
}

function PageRow({
  page,
  depth,
  hasChildren,
  isCollapsed,
  isFirst,
  isLast,
  flat,
  canEdit,
  selectedId,
  onSelect,
  onToggleExpand,
  onAddChild,
  onRequestMove,
  onShare,
  draggingId,
  setDraggingId,
  onMove,
  onMoveStep,
}: SharedTreeProps & {
  page: WikiPage;
  depth: number;
  hasChildren: boolean;
  isCollapsed?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const update = pagesHooks.useUpdate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(page.title);
  const [dropHint, setDropHint] = useState<'before' | 'after' | 'inside' | null>(null);
  const draggable = !flat && canEdit && !page.is_archived;

  function startEditing() {
    if (page.is_archived || !canEdit) return;
    setDraft(page.title);
    setEditing(true);
  }

  function commit() {
    const next = draft.trim();
    if (next && next !== page.title) update.mutate({ id: page.public_id, input: { title: next } });
    setEditing(false);
  }

  return (
    <div
      draggable={draggable && !editing}
      onDragStart={(e) => {
        dragOrigin = { x: e.clientX, y: e.clientY };
        setDraggingId(page.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        setDraggingId(null);
        setDropHint(null);
      }}
      onDragOver={(e) => {
        if (draggingId == null || draggingId === page.id || !draggable) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        // Narrow "inside" band (middle 30%) so most drops reorder, not re-parent.
        setDropHint(y < rect.height * 0.35 ? 'before' : y > rect.height * 0.65 ? 'after' : 'inside');
      }}
      onDragLeave={() => setDropHint(null)}
      onDrop={(e) => {
        e.preventDefault();
        const moved = dragOrigin
          ? Math.hypot(e.clientX - dragOrigin.x, e.clientY - dragOrigin.y)
          : Infinity;
        if (moved >= 6 && draggingId != null && dropHint) onMove(draggingId, page.id, dropHint);
        dragOrigin = null;
        setDropHint(null);
        setDraggingId(null);
      }}
      className={cn(
        'group flex cursor-pointer items-center gap-0.5 rounded pr-1',
        page.id === selectedId ? 'bg-muted' : 'hover:bg-muted/50',
        dropHint === 'inside' && 'ring-primary ring-1',
        dropHint === 'before' && 'border-primary border-t-2',
        dropHint === 'after' && 'border-primary border-b-2',
      )}
      style={{ paddingLeft: `${depth * 14}px` }}
    >
      {hasChildren ? (
        <button
          type="button"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={() => onToggleExpand(page.id)}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded p-0.5"
        >
          {isCollapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
      ) : (
        <span className="w-[18px] shrink-0" />
      )}
      {editing ? (
        <span className="flex min-w-0 flex-1 items-center gap-1.5 py-0.5">
          <span className="text-sm">{page.icon || '📄'}</span>
          <input
            ref={(el) => el?.focus()}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') setEditing(false);
            }}
            className="border-input bg-background min-w-0 flex-1 rounded border px-1 py-0.5 text-sm outline-none"
          />
        </span>
      ) : (
        <button
          type="button"
          // Row click selects the page and, if it has collapsed children,
          // expands them too — the chevron stays as the explicit toggle.
          onClick={() => {
            onSelect(page.id);
            if (hasChildren && isCollapsed) onToggleExpand(page.id);
          }}
          onDoubleClick={startEditing}
          title={canEdit ? 'Double-click to rename' : undefined}
          className={cn(
            'flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 py-1 text-left text-sm',
            page.id === selectedId && 'font-medium',
          )}
        >
          <span className="text-sm">{page.icon || '📄'}</span>
          <span className="flex-1 truncate">{page.title || 'Untitled'}</span>
          {page.published_at ? (
            <span
              className="size-1.5 shrink-0 rounded-full bg-emerald-500"
              title="Published — visible to everyone who can access this page"
              aria-label="Published"
            />
          ) : (
            <span
              className="bg-muted-foreground/40 size-1.5 shrink-0 rounded-full"
              title="Draft — only you can see it until you publish"
              aria-label="Draft"
            />
          )}
          {page.is_locked ? (
            <span className="shrink-0" title="Locked — unlock to edit" aria-label="Locked">
              <Lock className="text-muted-foreground size-3" />
            </span>
          ) : null}
        </button>
      )}
      {canEdit ? (
        <PageRowMenu
          page={page}
          onAddChild={onAddChild}
          onRename={startEditing}
          onMoveTo={() => onRequestMove(page)}
          onShare={onShare}
          onMoveUp={!flat && !isFirst ? () => onMoveStep(page, 'up') : undefined}
          onMoveDown={!flat && !isLast ? () => onMoveStep(page, 'down') : undefined}
        />
      ) : null}
    </div>
  );
}

function PageRowMenu({
  page,
  onAddChild,
  onRename,
  onMoveTo,
  onShare,
  onMoveUp,
  onMoveDown,
}: {
  page: WikiPage;
  onAddChild: (parentId: number) => void;
  onRename: () => void;
  onMoveTo: () => void;
  onShare?: (page: WikiPage) => void;
  /** Present + enabled only when the page has a sibling above/below. */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const update = pagesHooks.useUpdate();
  const remove = pagesHooks.useRemove();
  const sticky = stickiesHooks.useCreate();
  const archive = usePageAction((id: string) => pagesApi.archive(id));
  const restore = usePageAction((id: string) => pagesApi.restore(id));
  const publish = usePageAction((id: string) => pagesApi.publish(id));
  const unpublish = usePageAction((id: string) => pagesApi.unpublish(id));
  const patch = (input: Partial<WikiPage>) => update.mutate({ id: page.public_id, input });
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${page.title || 'page'}`}
            className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {page.is_archived ? (
            <>
              <DropdownMenuItem onClick={() => restore.mutate(page.public_id)}>
                <RotateCcw className="size-4" /> Restore
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="size-4" /> Delete
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem onClick={onRename}>
                <Pencil className="size-4" /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAddChild(page.id)}>
                <Plus className="size-4" /> Add sub-page
              </DropdownMenuItem>
              {onMoveUp ? (
                <DropdownMenuItem onClick={onMoveUp}>
                  <ArrowUp className="size-4" /> Move up
                </DropdownMenuItem>
              ) : null}
              {onMoveDown ? (
                <DropdownMenuItem onClick={onMoveDown}>
                  <ArrowDown className="size-4" /> Move down
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={onMoveTo}>
                <FolderInput className="size-4" /> Move to…
              </DropdownMenuItem>
              {onShare ? (
                <DropdownMenuItem onClick={() => onShare(page)}>
                  <Share2 className="size-4" /> Share…
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={() => sticky.mutate({ page: page.id })}>
                <Pin className="size-4" /> Pin to Home
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => patch({ visibility: page.visibility === 'public' ? 'private' : 'public' })}
              >
                {page.visibility === 'public' ? <EyeOff className="size-4" /> : <Eye className="size-4" />} Make{' '}
                {page.visibility === 'public' ? 'private' : 'public'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => patch({ is_locked: !page.is_locked })}>
                {page.is_locked ? <Unlock className="size-4" /> : <Lock className="size-4" />}
                {page.is_locked ? 'Unlock' : 'Lock'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  page.published_at ? unpublish.mutate(page.public_id) : publish.mutate(page.public_id)
                }
              >
                {page.published_at ? <GlobeLock className="size-4" /> : <Globe className="size-4" />}{' '}
                {page.published_at ? 'Unpublish' : 'Publish'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={() => archive.mutate(page.public_id)}>
                <Archive className="size-4" /> Archive
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{page.title || 'Untitled'}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the page and any sub-pages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate(page.public_id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function MoveDialog({
  page,
  excludeIds,
  pages,
  onMove,
  onClose,
}: {
  page: WikiPage | null;
  excludeIds: Set<number>;
  pages: WikiPage[];
  onMove: (page: WikiPage, parent: number | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const candidates = pages.filter(
    (p) => !excludeIds.has(p.id) && (p.title || 'Untitled').toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <Dialog open={page !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move “{page?.title || 'Untitled'}”</DialogTitle>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…" className="h-9" />
        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          <button
            type="button"
            onClick={() => page && onMove(page, null)}
            className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
          >
            <span className="text-muted-foreground">↑</span> Top level
          </button>
          {candidates.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => page && onMove(page, p.id)}
              className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
            >
              <span>{p.icon || '📄'}</span>
              <span className="truncate">{p.title || 'Untitled'}</span>
            </button>
          ))}
          {candidates.length === 0 ? (
            <p className="text-muted-foreground px-2 py-2 text-xs">No matching pages.</p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
